import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Edit2 } from "iconsax-react";
import { Badge, Table } from "@/shared/ui";
import { Pagination } from "@/shared/components";
import { usePagination } from "@/shared/hooks";
import { formatDateForDisplay, parseDate } from "@/shared/utils";
import { RequestDetailsModal, isRevisionRequested } from "@/features/self/requests";
import type { SelfServiceRequest } from "@/features/self/requests";
import { getStatusBadgeColor, getStatusIcon, resolveLeaveRequestStatusLabel } from "@/features/self/shared/utils/statusUtils";
import type { WorkerDetailResponse } from "../types";

interface Props {
  worker: WorkerDetailResponse;
}

type SortKey = "type" | "status" | "created_at" | "period";
interface SortConfig {
  key: SortKey;
  direction: "asc" | "desc";
}

function resolveStatusLabel(
  request: SelfServiceRequest,
  locale: "ru" | "kk" | "en",
  t: (key: string) => string,
  tPortal: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (isRevisionRequested(request)) return tPortal("requests.status.revisionRequested");
  const leaveLabel = resolveLeaveRequestStatusLabel(
    request.type,
    request.status,
    request.date_range?.start_date,
    tPortal,
  );
  if (leaveLabel) return leaveLabel;
  const fromApi = request.status_label?.[locale] || request.status_label?.ru;
  if (fromApi && fromApi !== request.status) return fromApi;
  const key = `requests.statusLabels.${request.status}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return request.status;
}

function toggleSort(current: SortConfig | null, newKey: SortKey): SortConfig {
  if (current?.key === newKey) {
    return { key: newKey, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { key: newKey, direction: "asc" };
}

export default function RequestsTab({ worker }: Props) {
  const { t, i18n } = useTranslation("WorkerDetailPage");
  const { t: tPortal } = useTranslation("EmployeePortal");
  const locale = i18n.language as "ru" | "kk" | "en";
  const [selectedRequest, setSelectedRequest] = useState<SelfServiceRequest | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const requests = worker.requests ?? [];

  const sortedRequests = useMemo(() => {
    if (!sortConfig) return requests;
    const { key, direction } = sortConfig;
    return [...requests].sort((a, b) => {
      let cmp = 0;
      if (key === "type") {
        const aLabel = a.type_label?.[locale] || a.type_label?.ru || a.type;
        const bLabel = b.type_label?.[locale] || b.type_label?.ru || b.type;
        cmp = aLabel.localeCompare(bLabel, "ru");
      } else if (key === "status") {
        const aLabel = a.status_label?.[locale] || a.status_label?.ru || a.status;
        const bLabel = b.status_label?.[locale] || b.status_label?.ru || b.status;
        cmp = aLabel.localeCompare(bLabel, "ru");
      } else if (key === "created_at") {
        const aDate = parseDate(a.created_at);
        const bDate = parseDate(b.created_at);
        cmp = (aDate?.getTime() ?? 0) - (bDate?.getTime() ?? 0);
      } else if (key === "period") {
        const aDate = parseDate(a.date_range?.start_date ?? null);
        const bDate = parseDate(b.date_range?.start_date ?? null);
        cmp = (aDate?.getTime() ?? 0) - (bDate?.getTime() ?? 0);
      }
      return direction === "asc" ? cmp : -cmp;
    });
  }, [requests, sortConfig, locale]);

  const pagination = usePagination(sortedRequests.length, 10, [sortedRequests.length]);
  const pageRequests = sortedRequests.slice(pagination.startIndex, pagination.endIndex);

  if (requests.length === 0) {
    return (
      <div className="p-7 radius-3xl surface-base-fill flex flex-col items-center justify-center gap-1 py-16">
        <p className="text-body-bold-md content-base-primary">{t("requests.noRequests")}</p>
        <p className="text-body-regular-sm content-base-secondary">{t("requests.noRequestsHint")}</p>
      </div>
    );
  }

  function formatPeriod(request: SelfServiceRequest): string {
    if (!request.date_range) return "—";
    const start = formatDateForDisplay(request.date_range.start_date);
    const end = formatDateForDisplay(request.date_range.end_date);
    return `${start} – ${end}`;
  }

  function handleSort(key: SortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  return (
    <div className="p-7 radius-3xl surface-base-fill flex flex-col gap-4">
      <div className="overflow-x-auto page-scroll pb-2 min-w-0">
        <Table.Table>
          <Table.Header>
            <tr>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "type" ? sortConfig.direction : undefined}
                onSort={() => handleSort("type")}>
                {t("requests.columns.type")}
              </Table.HeadCell>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "status" ? sortConfig.direction : undefined}
                onSort={() => handleSort("status")}>
                {t("requests.columns.status")}
              </Table.HeadCell>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "created_at" ? sortConfig.direction : undefined}
                onSort={() => handleSort("created_at")}>
                {t("requests.columns.createdAt")}
              </Table.HeadCell>
              <Table.HeadCell
                sortable
                sortDirection={sortConfig?.key === "period" ? sortConfig.direction : undefined}
                onSort={() => handleSort("period")}>
                {t("requests.columns.period")}
              </Table.HeadCell>
            </tr>
          </Table.Header>
          <Table.Body>
            {pageRequests.map(function renderRow(request) {
              const typeLabel = request.type_label
                ? request.type_label[locale] || request.type_label.ru
                : request.type;
              const revision = isRevisionRequested(request);
              const statusLabel = resolveStatusLabel(request, locale, t, tPortal);

              return (
                <Table.Row
                  key={request.id}
                  onClick={() => setSelectedRequest(request)}
                  className="cursor-pointer">
                  <Table.Cell isBold>{typeLabel}</Table.Cell>
                  <Table.Cell>
                    <Badge
                      variant="soft"
                      color={revision ? "notice" : getStatusBadgeColor(request.status)}
                      text={statusLabel}
                      icon={revision ? <Edit2 size={12} color="currentColor" /> : getStatusIcon(request.status)}
                    />
                  </Table.Cell>
                  <Table.Cell>{formatDateForDisplay(request.created_at) || "—"}</Table.Cell>
                  <Table.Cell>{formatPeriod(request)}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Table>
      </div>

      {pagination.totalPages > 1 && (
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={pagination.setPage}
          fromItem={pagination.fromItem}
          toItem={pagination.toItem}
          total={sortedRequests.length}
        />
      )}

      {selectedRequest && (
        <RequestDetailsModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          readOnly
        />
      )}
    </div>
  );
}
